import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Pencil, RotateCcw, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Label, Select, Textarea } from '@/components/ui/Input';
import { Banner } from '@/components/ui/Banner';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { useAuthStore } from '@/stores/auth';
import { displayName } from '@shared/prospect';
import {
  EVENT_TYPE_LABELS,
  eventsApi,
  type CreateEventPayload,
  type EventItem,
  type EventType,
  type UpdateEventPayload,
} from '@/services/events.api';
import { usersApi, type TeamMember } from '@/services/users.api';
import { opportunitiesApi } from '@/services/opportunities.api';

interface Props {
  /** --- Mode création (historique) --- */
  prospectId?: string;
  opportunityId?: string;
  defaultType?: EventType;
  /** Invoqué au submit en mode création : le parent fait l'appel API. */
  onSubmit?: (payload: CreateEventPayload) => Promise<void>;

  /** --- Mode vue/édition --- */
  event?: EventItem;
  /** Invoqué après un update / delete / complete réussi. Le parent ré-fetche
   *  la liste des événements. */
  onChange?: () => void;

  /** --- Commun --- */
  onClose: () => void;
}

type Mode = 'create' | 'view' | 'edit';

/**
 * Modale événement - multi-modes :
 *  - `create` : formulaire vierge (historique `prospectId + onSubmit`).
 *  - `view` : lecture seule des détails d'un événement existant (champs
 *    `event` fourni). Boutons : Fermer, [Modifier], [Marquer faite], [Supprimer].
 *  - `edit` : même formulaire que `create` mais pré-rempli et pointant sur
 *    `PATCH /events/:id`. Accessible depuis view via « Modifier ».
 *
 * Le formulaire s'adapte au `type` de l'événement (call / meeting / task / …).
 */
export function EventFormModal({
  prospectId,
  opportunityId,
  defaultType = 'note',
  onSubmit,
  event,
  onChange,
  onClose,
}: Props) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const isCreating = !event;
  const [mode, setMode] = useState<Mode>(isCreating ? 'create' : 'view');

  // Valeurs courantes (hydratées depuis `event` si fourni). En mode create,
  // initial vide. En edit, on repart toujours de l'event le plus récent.
  const [type, setType] = useState<EventType>(event?.type ?? defaultType);
  // Champ « Action » (ex-titre). Textarea multiligne, 4 lignes / 400 chars.
  const [title, setTitle] = useState(event?.title ?? '');
  // Direction & durée d'appel : UI masquée V1 (cf bloc `type === 'call'`).
  // On garde la colonne BDD + l'API, mais plus de state React - le submit
  // pour un `call` envoie `callDirection: 'outbound'` par défaut.
  const [meetingType, setMeetingType] = useState<'physical' | 'video'>(
    event?.meetingType ?? 'physical',
  );
  const [meetingLocation, setMeetingLocation] = useState(event?.meetingLocation ?? '');
  const [scheduledAt, setScheduledAt] = useState(
    event?.scheduledAt
      ? toLocalDatetimeInput(event.scheduledAt)
      : // Création : pour call/email on pré-remplit avec "now" dès le premier
        // render. Permet à l'utilisateur de saisir rapidement un appel qu'il
        // vient de passer OU de corriger si c'est un ancien mail.
        isCreating && (defaultType === 'call' || defaultType === 'email')
        ? nowLocalDatetimeInput()
        : '',
  );
  const [taskDueDate, setTaskDueDate] = useState(
    event?.taskDueDate ? toLocalDatetimeInput(event.taskDueDate) : '',
  );
  // Pré-remplissage par défaut : current user. L'utilisateur peut réassigner
  // explicitement via le select.
  const [taskAssignedTo, setTaskAssignedTo] = useState(
    event?.taskAssignedTo ?? currentUserId ?? '',
  );
  // Rattachement d'opportunité (RDV uniquement — décision produit 2026-06-05).
  // Valeur initiale : l'opp de l'événement (view/edit) sinon la prop
  // `opportunityId` (création depuis la fiche opp → pré-rempli ; depuis la
  // fiche prospect → vide). '' = aucune opportunité (rattaché au prospect seul).
  const [opportunityIdState, setOpportunityIdState] = useState<string>(
    event?.opportunityId ?? opportunityId ?? '',
  );
  // Options du dropdown : opportunités du prospect concerné. Chargées seulement
  // pour les RDV (type=meeting).
  const [oppOptions, setOppOptions] = useState<
    Array<{ id: string; reference: string; code: string }>
  >([]);
  // Prospect de référence pour lister ses opportunités : prop en création,
  // sinon le prospect de l'événement (view/edit).
  const resolvedProspectId = prospectId ?? event?.prospect.id;

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(true, dialogRef);
  // Décision produit 2026-05-23 : seul le clic backdrop est désactivé.
  // Escape reste actif (a11y WCAG, cohérence avec les autres modales du CRM).
  // La fermeture est aussi possible via le bouton X header.
  useHotkey('escape', onClose, { allowInInput: true });
  const toast = useToast();
  const confirm = useConfirm();

  // Pré-remplissage `now` pour call/email en mode création : déclenché quand
  // l'utilisateur SWITCH vers ces types après coup (ex: démarre sur note puis
  // bascule sur email). On ne touche rien si l'utilisateur a déjà saisi une
  // valeur OU si on est en mode edit/view (préserver la donnée originale).
  useEffect(() => {
    if (mode !== 'create') return;
    if (type !== 'call' && type !== 'email') return;
    if (scheduledAt) return;
    setScheduledAt(nowLocalDatetimeInput());
    // scheduledAt est volontairement omis pour ne pas re-déclencher l'effet
    // après qu'on l'a posé. Seul un changement de `type` doit en déclencher
    // une nouvelle évaluation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, mode]);

  const token = useAuthStore((s) => s.accessToken);
  useEffect(() => {
    if (!token) return;
    // Chargement des membres d'équipe pour le dropdown d'assignation (tâche).
    // Chargé systématiquement : même en view mode on peut afficher un label
    // (ex. « Assigné à : Jean Dupont » au lieu de l'UUID brut).
    let cancelled = false;
    usersApi
      .teamMembers(token)
      .then((list) => {
        if (!cancelled) setMembers(list);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Chargement des opportunités du prospect pour le dropdown RDV. Déclenché
  // uniquement pour les RDV (type=meeting) afin d'éviter un fetch inutile sur
  // les autres types. Inclut toutes les opps du prospect (terminales comprises)
  // - le commercial peut vouloir rattacher un RDV à n'importe quelle opp.
  useEffect(() => {
    if (!token || !resolvedProspectId || type !== 'meeting') return;
    let cancelled = false;
    opportunitiesApi
      .list(token, { prospect_id: resolvedProspectId, limit: 100 })
      .then((res) => {
        if (cancelled) return;
        setOppOptions(
          res.data.map((o) => ({
            id: o.id,
            reference: o.reference,
            code: o.contractType.code,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setOppOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, resolvedProspectId, type]);

  // Bascule view → edit : on réinitialise les champs depuis l'event courant
  // (au cas où un reload aurait mis à jour les valeurs entre-temps).
  function enterEdit() {
    if (!event) return;
    setType(event.type);
    setTitle(event.title ?? '');
    setMeetingType(event.meetingType ?? 'physical');
    setMeetingLocation(event.meetingLocation ?? '');
    setScheduledAt(event.scheduledAt ? toLocalDatetimeInput(event.scheduledAt) : '');
    setTaskDueDate(event.taskDueDate ? toLocalDatetimeInput(event.taskDueDate) : '');
    setTaskAssignedTo(event.taskAssignedTo ?? currentUserId ?? '');
    setOpportunityIdState(event.opportunityId ?? '');
    setError(null);
    setMode('edit');
  }

  function cancelEdit() {
    setError(null);
    setMode('view');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'create') {
        if (!prospectId) throw new Error('prospectId manquant');
        if (!onSubmit) throw new Error('onSubmit manquant');
        const payload: CreateEventPayload = {
          type,
          prospectId,
          // Rattachement d'opp : pour les RDV, vient du dropdown
          // (`opportunityIdState`) ; pour les autres types, `opportunityIdState`
          // est resté égal à la prop `opportunityId` (dropdown non affiché).
          opportunityId: opportunityIdState || undefined,
          title: title.trim() || undefined,
        };
        if (type === 'call') {
          // Direction & durée cachées V1 - défaut `outbound` pour satisfaire
          // la validation Zod serveur (`callDirection` requis sur type=call).
          payload.callDirection = 'outbound';
          // L'heure de l'appel est stockée dans `scheduledAt` (colonne
          // générique partagée avec les RDV).
          if (scheduledAt) payload.scheduledAt = new Date(scheduledAt).toISOString();
        }
        if (type === 'meeting') {
          payload.meetingType = meetingType;
          if (meetingLocation.trim()) payload.meetingLocation = meetingLocation.trim();
          if (!scheduledAt) throw new Error('Date du RDV requise');
          payload.scheduledAt = new Date(scheduledAt).toISOString();
        }
        if (type === 'task') {
          if (!taskDueDate) throw new Error("Date d'échéance requise");
          payload.taskDueDate = new Date(taskDueDate).toISOString();
          if (taskAssignedTo.trim()) payload.taskAssignedTo = taskAssignedTo.trim();
        }
        if (type === 'email') {
          // Date du mail stockée dans `scheduledAt` (colonne générique).
          // Permet de saisir un mail reçu il y a plusieurs jours et de le
          // faire apparaître chronologiquement au bon endroit dans la
          // timeline (tri backend : COALESCE(scheduled_at, task_due_date, created_at) DESC).
          if (scheduledAt) payload.scheduledAt = new Date(scheduledAt).toISOString();
        }
        await onSubmit(payload);
        return;
      }

      // mode === 'edit'
      if (!event || !token) throw new Error('Contexte edit invalide');
      const payload: UpdateEventPayload = {
        title: title.trim() || undefined,
      };
      if (event.type === 'call') {
        // UI cachée V1 : on ne transmet pas direction/durée côté edit
        // (évite un ré-écrasement par les valeurs par défaut de l'état local).
        if (scheduledAt) payload.scheduledAt = new Date(scheduledAt).toISOString();
      }
      if (event.type === 'meeting') {
        payload.meetingType = meetingType;
        if (meetingLocation.trim()) payload.meetingLocation = meetingLocation.trim();
        if (scheduledAt) payload.scheduledAt = new Date(scheduledAt).toISOString();
        // Rattachement d'opp éditable : '' → null (délier). PAS d'auto-
        // avancement d'étape côté backend (décision produit 2026-06-05).
        payload.opportunityId = opportunityIdState || null;
      }
      if (event.type === 'task') {
        if (taskDueDate) payload.taskDueDate = new Date(taskDueDate).toISOString();
        if (taskAssignedTo) payload.taskAssignedTo = taskAssignedTo;
      }
      if (event.type === 'email') {
        if (scheduledAt) payload.scheduledAt = new Date(scheduledAt).toISOString();
      }
      await eventsApi.update(token, event.id, payload);
      toast({ tone: 'success', message: 'Événement mis à jour.' });
      onChange?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  async function handleComplete() {
    if (!event || !token) return;
    setSubmitting(true);
    try {
      await eventsApi.complete(token, event.id);
      toast({ tone: 'success', message: 'Tâche marquée comme faite.' });
      onChange?.();
      onClose();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Opération impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
      setSubmitting(false);
    }
  }

  /**
   * Annulation d'une complétion : on repasse `taskStatus` à `todo`. Le
   * backend `updateEvent` remet automatiquement `completedAt` à null quand
   * taskStatus !== 'done' (cf events.service.ts). Permet de corriger une
   * erreur de clic "Marquer faite".
   */
  async function handleUncomplete() {
    if (!event || !token) return;
    setSubmitting(true);
    try {
      await eventsApi.update(token, event.id, { taskStatus: 'todo' });
      toast({ tone: 'success', message: 'Tâche remise à faire.' });
      onChange?.();
      onClose();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Opération impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!event || !token) return;
    const ok = await confirm({
      title: 'Supprimer cet événement ?',
      message: 'Cette action est irréversible.',
      variant: 'danger',
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      await eventsApi.delete(token, event.id);
      toast({ tone: 'success', message: 'Événement supprimé.' });
      onChange?.();
      onClose();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Suppression impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
      setSubmitting(false);
    }
  }

  const readOnly = mode === 'view';

  // `aria-label` a besoin d'un string plat (a11y), tandis que le titre
  // rendu peut contenir un lien cliquable vers la fiche prospect/opp.
  const ariaLabel =
    mode === 'create'
      ? 'Ajouter un événement'
      : mode === 'edit'
        ? `Modifier · ${EVENT_TYPE_LABELS[event!.type]}`
        : `${EVENT_TYPE_LABELS[event!.type]} - ${displayName(event!.prospect)}`;

  // En mode view, le nom du prospect est cliquable et mène à la fiche
  // prospect ; si l'événement est rattaché à une opportunité, on ouvre
  // directement cette opp via deep-link (onglet + ?opportunity=).
  //
  // On lit `opportunityId` (FK brute, toujours présente quand posée) plutôt
  // que `opportunity?.id` (relation incluse - normalement peuplée mais plus
  // fragile en cas de select tronqué). On passe `to` en objet explicite
  // `{ pathname, search }` : certains builds de react-router route
  // différemment les strings avec query-string quand l'host est le même
  // (navigation interne « silencieuse » qui perd parfois la query).
  const prospectLinkTo = event
    ? {
        pathname: `/prospects/${event.prospect.id}`,
        search: event.opportunityId ? `?tab=opportunities&opportunity=${event.opportunityId}` : '',
      }
    : null;

  // Options du dropdown opportunité (RDV). On garantit que l'opp actuellement
  // rattachée est présente même si la liste (cap 100) ne la contient pas ou
  // n'est pas encore chargée — évite un Select qui afficherait « vide » alors
  // qu'une opp est bien liée.
  const meetingOppOptions = (() => {
    const opts = [...oppOptions];
    if (
      opportunityIdState &&
      !opts.some((o) => o.id === opportunityIdState) &&
      event?.opportunity &&
      event.opportunity.id === opportunityIdState
    ) {
      opts.unshift({ id: event.opportunity.id, reference: event.opportunity.reference, code: '' });
    }
    return opts;
  })();

  // En view mode, `type` est fixe (pas modifiable) ; en create, type est libre ;
  // en edit, on fige le type aussi (changer le type d'un event existant est
  // hors-scope - ça bouleverserait les champs associés).
  const typeLocked = mode !== 'create';

  return (
    <div
      className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm"
      // Décision produit 2026-05-22 : pas de fermeture sur clic backdrop.
      // La seule façon de fermer la modale est le bouton X en haut à droite.
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className="shadow-modal animate-scale-in w-full max-w-lg rounded-lg bg-white"
      >
        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="border-border-subtle flex items-start justify-between gap-3 border-b pb-4">
            <div className="min-w-0 flex-1">
              <h2 className="text-brand truncate text-lg font-semibold">
                {mode === 'create' && 'Ajouter un événement'}
                {mode === 'edit' && event && <>Modifier · {EVENT_TYPE_LABELS[event.type]}</>}
                {mode === 'view' && event && (
                  <>
                    {EVENT_TYPE_LABELS[event.type]} -{' '}
                    {prospectLinkTo && (
                      <Link
                        to={prospectLinkTo}
                        onClick={onClose}
                        // Bleu profond pour signaler la cliquabilité. Soulignement
                        // uniquement au hover pour rester sobre à la lecture.
                        className="text-blue-700 hover:text-blue-900 hover:underline"
                      >
                        {displayName(event.prospect)}
                      </Link>
                    )}
                  </>
                )}
              </h2>
              {mode === 'create' && (
                <p className="text-text-muted mt-0.5 text-xs">
                  Le formulaire s&apos;adapte au type sélectionné.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-text-muted hover:text-brand rounded-md p-1 hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div>
            <Label htmlFor="type" required>
              Type
            </Label>
            <Select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value as EventType)}
              disabled={typeLocked || readOnly}
            >
              {(Object.keys(EVENT_TYPE_LABELS) as EventType[]).map((t) => (
                <option key={t} value={t}>
                  {EVENT_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          </div>

          {type === 'call' && (
            <div>
              <Label htmlFor="call-at" required>
                Heure de l&apos;appel
              </Label>
              <Input
                id="call-at"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                disabled={readOnly}
              />
              {/* Champs Direction & Durée masqués V1 - conservés côté schéma
                  BDD + API. Le backend exige `callDirection` pour un `call` :
                  on envoie un défaut `outbound` côté submit (cf handleSubmit). */}
            </div>
          )}

          {type === 'meeting' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="mtype">Type</Label>
                  <Select
                    id="mtype"
                    value={meetingType}
                    onChange={(e) => setMeetingType(e.target.value as 'physical' | 'video')}
                    disabled={readOnly}
                  >
                    <option value="physical">Présentiel</option>
                    <option value="video">Visio</option>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="mloc">Lieu / lien</Label>
                  <Input
                    id="mloc"
                    value={meetingLocation}
                    onChange={(e) => setMeetingLocation(e.target.value)}
                    disabled={readOnly}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="sched" required>
                  Date / heure
                </Label>
                <Input
                  id="sched"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  disabled={readOnly}
                />
              </div>
              {/* Rattachement d'opportunité (RDV uniquement). Vide = rattaché
                  au prospect seul. Pré-rempli sur l'opp courante si le RDV est
                  créé/ouvert depuis une fiche opportunité. */}
              <div>
                <Label htmlFor="opp">Opportunité</Label>
                <Select
                  id="opp"
                  value={opportunityIdState}
                  onChange={(e) => setOpportunityIdState(e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">Aucune (rattaché au prospect)</option>
                  {meetingOppOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.reference}
                      {o.code ? ` · ${o.code}` : ''}
                    </option>
                  ))}
                </Select>
              </div>
            </>
          )}

          {type === 'task' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="due" required>
                    Échéance
                  </Label>
                  <Input
                    id="due"
                    type="datetime-local"
                    value={taskDueDate}
                    onChange={(e) => setTaskDueDate(e.target.value)}
                    disabled={readOnly}
                  />
                </div>
                <div>
                  <Label htmlFor="assignee">Assigné à</Label>
                  <Select
                    id="assignee"
                    value={taskAssignedTo}
                    onChange={(e) => setTaskAssignedTo(e.target.value)}
                    disabled={readOnly}
                  >
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.firstName} {m.lastName} · {m.email}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </>
          )}

          {type === 'email' && (
            <div>
              <Label htmlFor="email-at">Date du mail</Label>
              <Input
                id="email-at"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                disabled={readOnly}
              />
              <p className="text-text-muted mt-1 text-xs">
                Par défaut maintenant. Modifiez si vous loggez un mail reçu il y a plusieurs jours -
                il apparaîtra à la bonne date dans la timeline.
              </p>
            </div>
          )}

          {/* Champ « Action » placé en dernier (après les blocs
              type-spécifiques) pour laisser à l'utilisateur la zone de saisie
              la plus large une fois les métadonnées renseignées. */}
          <div>
            <Label htmlFor="title">Action</Label>
            <Textarea
              id="title"
              rows={12}
              maxLength={5000}
              value={title}
              onChange={(e) => {
                // Limite stricte à 50 lignes (décision produit 2026-06-05) : on
                // refuse la saisie d'un 51ème retour à la ligne. Les autres
                // contraintes sont gérées par `maxLength` (5000).
                const next = e.target.value;
                if (next.split('\n').length > 50) return;
                setTitle(next);
              }}
              placeholder="Ex : Rappeler M. Martin, compte-rendu de RDV, relance devis…"
              disabled={readOnly}
              className="max-h-[50vh]"
            />
            {!readOnly && (
              <p className="mt-1 text-[11px] text-text-muted">
                {title.length}/5000 · {title.split('\n').length}/50 lignes
              </p>
            )}
          </div>

          {/* Validation visuelle pour les entités task-like (refonte 2026-05-21,
              Q3 : les meetings sont task-like et affichent aussi "validé"). */}
          {event &&
            (event.type === 'task' || event.type === 'meeting') &&
            event.taskStatus === 'done' &&
            event.completedAt && (
              <p className="text-success-600 text-xs">
                ✓ {event.type === 'meeting' ? 'RDV validé' : 'Tâche validée'} le{' '}
                {new Date(event.completedAt).toLocaleString('fr-FR', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {event.completer && ` par ${event.completer.firstName} ${event.completer.lastName}`}
              </p>
            )}

          {error && <Banner tone="danger">{error}</Banner>}

          {/* Footer dynamique selon le mode */}
          <div className="border-border-subtle flex flex-wrap justify-end gap-2 border-t pt-4">
            {mode === 'view' && event && (
              <>
                <Button variant="secondary" onClick={onClose} type="button">
                  Fermer
                </Button>
                <Button
                  variant="ghost"
                  type="button"
                  leadingIcon={<Trash2 className="h-3.5 w-3.5" />}
                  onClick={handleDelete}
                  loading={submitting}
                >
                  Supprimer
                </Button>
                {/* Boutons « Marquer fait·e / non fait·e » exposés pour les
                    entités task-like (task et, depuis la refonte 2026-05-21,
                    meeting). Le libellé s'adapte au type (« faite » pour tâche,
                    « fait » pour RDV). */}
                {(event.type === 'task' || event.type === 'meeting') &&
                  event.taskStatus !== 'done' && (
                    <Button
                      variant="secondary"
                      type="button"
                      leadingIcon={<Check className="h-3.5 w-3.5" />}
                      onClick={handleComplete}
                      loading={submitting}
                    >
                      {event.type === 'meeting' ? 'Marquer fait' : 'Marquer faite'}
                    </Button>
                  )}
                {(event.type === 'task' || event.type === 'meeting') &&
                  event.taskStatus === 'done' && (
                    <Button
                      variant="secondary"
                      type="button"
                      leadingIcon={<RotateCcw className="h-3.5 w-3.5" />}
                      onClick={handleUncomplete}
                      loading={submitting}
                    >
                      {event.type === 'meeting' ? 'Marquer non fait' : 'Marquer non faite'}
                    </Button>
                  )}
                <Button
                  variant="primary"
                  type="button"
                  leadingIcon={<Pencil className="h-3.5 w-3.5" />}
                  onClick={enterEdit}
                >
                  Modifier
                </Button>
              </>
            )}
            {mode === 'edit' && (
              <>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={cancelEdit}
                  disabled={submitting}
                >
                  Annuler
                </Button>
                <Button type="submit" variant="primary" loading={submitting}>
                  Enregistrer les modifications
                </Button>
              </>
            )}
            {mode === 'create' && (
              <>
                <Button variant="secondary" onClick={onClose} type="button">
                  Annuler
                </Button>
                <Button type="submit" variant="primary" loading={submitting}>
                  Enregistrer
                </Button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Convertit un ISO UTC en valeur acceptée par `<input type="datetime-local">`
 * (format `YYYY-MM-DDTHH:mm` en heure locale).
 */
function toLocalDatetimeInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Valeur "maintenant" au format `<input type="datetime-local">`. Utilisé pour
 * pré-remplir la date de call/email en mode création.
 */
function nowLocalDatetimeInput(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
